import React, { memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import HomeFooter from "@/components/Footer/HomeFooter";
import HomeNavbar from "@/components/NavBar/HomeNavBar";

import { getUser, logout } from "@/actions/auth";
import { resetUser } from "actions/auth";
import { useFetchUserData } from "@/hooks/useFetchUserData";
import useLoginSession from "@/hooks/useLoginSession";

import generalApi from "@/services/general-api";

import { getErrorMessage } from "utilities/error";
import { logGTMScreen, removeLogGTMUser } from "utilities/googleTagManager";

import SDALogo from "@/assets/sda-logo.svg";

function App() {
	const location = useLocation();
	const searchParam = location.search;

	const navigate = useNavigate();
	const mainContentRef = React.useRef(null);
	const { user: currentUser, isLoggedIn } = useSelector((state) => state.auth);
	const { isLoginBySession } = useLoginSession();
	useFetchUserData();

	React.useEffect(() => {
		logGTMScreen(location, isLoggedIn, currentUser);
		if (document.documentElement && document.scrollingElement && mainContentRef.current) {
			document.documentElement.scrollTop = 0;
			document.scrollingElement.scrollTop = 0;
			mainContentRef.current.scrollTop = 0;
			window.scrollTo(0, 0);
		}
	}, [location]);

	React.useEffect(() => {
		// Your condition to determine the initial page
		if (location.pathname === "/") {
			if (!currentUser) {
				navigate("/login" + searchParam, { replace: true });
			} else {
				if (currentUser && currentUser.user.has_registered_course) {
					navigate("/course" + searchParam, { replace: true });
				} else {
					navigate("/course" + searchParam, { replace: true });
				}
			}
		}
	}, [currentUser]);

	if (currentUser) {
		if (!currentUser.user.email_verified_at) {
			return <Navigate to={"/login/otp-validation" + searchParam} />;
		}
		if (currentUser.user.reset_password) {
			return <Navigate to={"/login" + searchParam} />;
		}
	}

	const eventsNumbersPattern = /events\/\d+/;
	const isEventsNumbersPage = eventsNumbersPattern.test(location.pathname);

	const isAvatarPage = location.pathname === "/avatar";

	const excludeFullWidthPages = {
		"/course": true,
		"/profile": true,
		"/certification": true,
		"/certification/view-cert": true,
		"/professional-cert/create": true,
		"/avatar": true,
		"/course/course-details": true,
	};

	let isExcludeFullWidthPage = excludeFullWidthPages[location.pathname.replace(/\/$/, "")];
	if (location.pathname.includes("/course/bundle")) {
		isExcludeFullWidthPage = true;
	}

	return (
		<>
			{!isLoginBySession && (
				<HomeNavbar
					logo={{
						innerLink: "/",
						imgSrc: SDALogo,
						imgAlt: "...",
					}}
				/>
			)}

			<div
				className={`main-content main-content-container ${
					location.pathname === "/home" ? "home-content-container" : ""
				}`}
				ref={mainContentRef}
				style={isExcludeFullWidthPage ? { backgroundColor: "#F0FDFF" } : {}}>
				{isExcludeFullWidthPage ? (
					<Outlet react={React} />
				) : (
					<Container
						className={"main-container pl-0 pr-0"}
						fluid
						style={isAvatarPage ? { backgroundColor: "#F0FDFF" } : {}}>
						<Outlet react={React} />
					</Container>
				)}
			</div>
			{isEventsNumbersPage ? null : <HomeFooter />}
			<NavigateFunctionComponent />
		</>
	);
}

const NavigateFunctionComponent = memo((props) => {
	/** Intercept any unauthorized request.
	 * dispatch logout action accordingly **/
	const dispatch = useDispatch();
	let navigate = useNavigate();

	const UNAUTHORIZED = 401;

	axios.interceptors.response.use(
		(response) => response,
		(error) => {
			if (axios.isAxiosError(error)) {
				if (!error.response) {
					return Promise.reject(error);
				}

				const { status } = error.response;
				let error_message = getErrorMessage(error);
				if (status === UNAUTHORIZED && error_message == "Unauthenticated.") {
					removeLogGTMUser();
					dispatch(resetUser());
					localStorage.removeItem("user");
					navigate("/login");
				}
				return Promise.reject(error);
			} else {
				return Promise.reject(error);
			}
		},
	);

	return <></>;
});

NavigateFunctionComponent.displayName = "NavigateFunctionComponent";

export default App;
